import { z } from 'zod';
import {
  booleanoDeQuerySchema,
  casasDecimaisPrecoSchema,
  CASAS_PRECO_PADRAO,
  custoUnitarioSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  listagemDeCadastroSchema,
  percentualSchema,
  precoUnitarioSchema,
  quantidadeSchema,
  respostaListaSchema,
  tipoInsumoSchema,
  uuidSchema,
} from './comum.ts';
import {
  aliquotaOpcionalSchema,
  codigoTributacaoMunicipalSchema,
  codigoTributacaoNacionalSchema,
  nbsSchema,
} from './fiscal.ts';
import { codigoNcmSchema } from './ncms.ts';

/**
 * Cadastros de compras (`.spec/010`, módulo Compras).
 *
 * Grupo de insumos e insumos. Insumos × empresa entra aqui quando o estoque
 * chegar — o saldo é do insumo, e os dois são sempre lidos juntos.
 */

/* -------------------------------------------------------------------------- */
/* Grupo de insumos                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Grupo de insumos: árvore de classificação.
 *
 * O cadastro tem quatro campos, e é o que a `.spec/010` lista: id, nome, pai e
 * situação. Sem código hierárquico e sem classificação — quem precisa disso é o
 * plano de contas, que responde à contabilidade. Aqui o nome é o identificador
 * que o usuário reconhece, e a posição na árvore é o que classifica.
 */
export const criarGrupoInsumoSchema = z.object({
  nome: z.string().trim().min(2, 'Informe o nome do grupo.').max(200),
  /** Nulo indica grupo de primeiro nível. */
  paiId: uuidSchema.nullable().prefault(null),
  ativo: z.boolean().prefault(true),
});

export type CriarGrupoInsumo = z.infer<typeof criarGrupoInsumoSchema>;
export const atualizarGrupoInsumoSchema = criarGrupoInsumoSchema;
export type AtualizarGrupoInsumo = z.infer<typeof atualizarGrupoInsumoSchema>;

export const grupoInsumoSchema = z.object({
  id: uuidSchema,
  nome: z.string(),
  paiId: uuidSchema.nullable(),
  /**
   * Nome do pai, desnormalizado na leitura.
   *
   * A listagem plana mostra a posição sem uma segunda consulta por linha — que,
   * numa página de 25, seriam 25 idas ao banco para exibir uma coluna.
   */
  paiNome: z.string().nullable(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type GrupoInsumo = z.infer<typeof grupoInsumoSchema>;

export const listaGruposInsumosSchema = respostaListaSchema(grupoInsumoSchema);
export type ListaGruposInsumos = z.infer<typeof listaGruposInsumosSchema>;

export const listarGruposInsumosQuerySchema = listagemDeCadastroSchema;
export type ListarGruposInsumosQuery = z.infer<typeof listarGruposInsumosQuerySchema>;

export const COLUNAS_ORDENAVEIS_GRUPO_INSUMO = ['nome', 'createdAt'] as const;

/* -------------------------------------------------------------------------- */
/* Árvore                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Nó da árvore de grupos.
 *
 * Recursivo, e por isso o tipo vem antes do schema: o Zod precisa da anotação
 * explícita para fechar o ciclo — `z.lazy` sozinho não consegue inferir um tipo
 * que se referencia.
 *
 * `quantidadeDeInsumos` é a contagem **do próprio nó**, sem o ramo abaixo dele.
 * É a soma do ramo que não existe, e não por esquecimento: insumo só se
 * classifica em grupo folha (`.spec/015`, decisão 14), então um nó com filhos
 * tem contagem zero por definição, e somar o ramo daria um número que não
 * corresponde a nenhuma linha do cadastro.
 *
 * Serve a duas telas. Na árvore, mostra onde o catálogo está concentrado. No
 * seletor de grupo pai, é o que permite tirar da lista os grupos que não podem
 * ganhar subnível — antes disso, o usuário só descobria ao gravar.
 */
export interface NoDaArvoreDeGrupos {
  id: string;
  nome: string;
  paiId: string | null;
  ativo: boolean;
  quantidadeDeInsumos: number;
  filhos: NoDaArvoreDeGrupos[];
}

export const noDaArvoreDeGruposSchema: z.ZodType<NoDaArvoreDeGrupos> = z.lazy(() =>
  z.object({
    id: uuidSchema,
    nome: z.string(),
    paiId: uuidSchema.nullable(),
    ativo: z.boolean(),
    quantidadeDeInsumos: z.number().int(),
    filhos: z.array(noDaArvoreDeGruposSchema),
  }),
);

/**
 * A árvore inteira, **sem paginação**.
 *
 * Árvore não pagina: uma página cortaria o ramo no meio e o usuário veria um
 * filho sem o pai dele. O cadastro é pequeno — dezenas de grupos —, então vem
 * completo numa consulta só.
 *
 * `total` acompanha para a tela poder avisar quando o cadastro passar do
 * tamanho que este desenho suporta. É a linha que dispara a troca para carga
 * por nível, e é melhor que ela apareça como número na tela do que como
 * lentidão inexplicada.
 */
export const arvoreDeGruposSchema = z.object({
  data: z.array(noDaArvoreDeGruposSchema),
  total: z.number().int(),
});

export type ArvoreDeGrupos = z.infer<typeof arvoreDeGruposSchema>;

/**
 * Teto de grupos que a tela de árvore carrega de uma vez.
 *
 * Não é limite do servidor — ele devolve o que houver. É o número a partir do
 * qual o desenho atual deixa de ser adequado, e existe aqui para que API e tela
 * concordem sobre quando avisar.
 */
export const LIMITE_ARVORE_DE_GRUPOS = 2000;

// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const listarArvoreDeGruposQuerySchema = z
  .object({
    /**
     * Incluir os inativos.
     *
     * Padrão é não incluir. Um grupo inativo com filhos ativos ainda aparece —
     * escondê-lo levaria os filhos junto, e o usuário veria um ramo sumir sem
     * explicação.
     */
    incluirInativos: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .transform((v) => v === true || v === 'true')
      .prefault(false),
  })
  .strict();

export type ListarArvoreDeGruposQuery = z.infer<typeof listarArvoreDeGruposQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Insumo                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Material ou serviço, no **mesmo cadastro** (`.spec/010`).
 *
 * O tipo não é decoração de tela: é ele que decide se o item movimenta estoque.
 * Só `MATERIAL` movimenta, e é também o único que admite NCM.
 *
 * O `z.enum` mora em `comum.ts`, junto com os outros códigos estáveis do
 * domínio, e aqui só se derivam a lista e o tipo. Redeclará-lo daria dois
 * lugares para acrescentar um código novo — e o esquecido seria justamente o
 * que a coluna `CHECK` do banco valida.
 */
export const TIPOS_INSUMO = tipoInsumoSchema.options;
export type TipoInsumo = z.infer<typeof tipoInsumoSchema>;

/** Rótulos de tela. Ficam no contrato para API e web nunca escreverem dois nomes. */
export const ROTULO_TIPO_INSUMO: Readonly<Record<TipoInsumo, string>> = {
  MATERIAL: 'Material',
  SERVICO: 'Serviço',
};

/**
 * O que cada tipo implica, para a dica do formulário.
 *
 * Escrito uma vez aqui e não em cada tela: a diferença entre os dois é regra de
 * estoque, não preferência de redação, e duas telas explicando com palavras
 * diferentes acabam explicando coisas diferentes.
 */
export const DICA_TIPO_INSUMO: Readonly<Record<TipoInsumo, string>> = {
  MATERIAL: 'Movimenta estoque e admite classificação fiscal por NCM.',
  SERVICO: 'Não movimenta estoque. Serviço não tem NCM — a nomenclatura é de mercadoria.',
};

/**
 * Comprimento máximo do nome.
 *
 * 300 porque a `.spec/010` exige suportar ao menos isso, e porque descrição de
 * material carrega marca, bitola, norma e acabamento: "Tubo PVC soldável 25mm
 * marrom NBR 5648 barra 6m" já passa de sessenta.
 */
export const TAMANHO_MAXIMO_NOME_INSUMO = 300;

/**
 * Comprimento máximo do **código reduzido** (`.spec/015`, decisão 49).
 *
 * Vinte, e não trezentos como o nome: os dois campos respondem a perguntas
 * opostas. O nome distingue dois materiais parecidos e por isso é longo; o
 * código é o que se digita no lugar dele, e um código que não caiba na cabeça
 * não é código — é um segundo nome.
 */
export const TAMANHO_MAXIMO_CODIGO_INSUMO = 20;

/**
 * O código reduzido: caixa alta, sem espaço, único na conta.
 *
 * **A caixa é normalizada, não recusada.** Quem digita `cim-01` grava `CIM-01`,
 * em vez de receber "use caixa alta" sobre um campo que o sistema sabe
 * corrigir. É a mesma escolha do `trim`: erro só onde não há resposta óbvia. O
 * `CHECK insumos_codigo_maiusculo` é a rede para quem não passa por aqui — um
 * script de importação —, e ele existe porque o índice único é sensível à
 * caixa: `cim-01` e `CIM-01` seriam dois códigos para o índice e um só para
 * quem lê.
 *
 * A regex roda **antes** do `toUpperCase()` e aceita as duas caixas de
 * propósito: recusar minúscula aqui tornaria a normalização inalcançável.
 *
 * **Sem espaço**, e os separadores são `. - / _`. Espaço é o que transforma
 * código em frase — "CIM 01" e "CIM  01" são o mesmo código para o olho e dois
 * para o índice único, e nada na tela explicaria a diferença. Os quatro
 * separadores cobrem as codificações que se vê na prática (`1042`, `CIM-01`,
 * `03.02.014`, `EL/220`).
 */
export const codigoInsumoSchema = z
  .string()
  .trim()
  .min(1, 'Informe o código do insumo.')
  .max(
    TAMANHO_MAXIMO_CODIGO_INSUMO,
    `O código tem no máximo ${TAMANHO_MAXIMO_CODIGO_INSUMO} caracteres — ele é o apelido curto do material, não um segundo nome.`,
  )
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9./_-]*$/,
    'O código aceita letras, números e os separadores . - / _ , começando por letra ou número. Sem espaço: "CIM 01" e "CIM  01" se leem igual e são códigos diferentes.',
  )
  .transform((valor) => valor.toUpperCase());

/**
 * O próximo código sugerido para um cadastro novo.
 *
 * Objeto, e não a string crua: a resposta de rota do projeto é sempre um objeto
 * JSON, e assim acrescentar "de onde saiu este número" amanhã não quebra o
 * cliente.
 */
export const proximoCodigoInsumoSchema = z.object({ codigo: z.string() });
export type ProximoCodigoInsumo = z.infer<typeof proximoCodigoInsumoSchema>;

/* -------------------------------------------------------------------------- */
/* Vínculo com empresa                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Quais empresas do grupo usam este insumo.
 *
 * O vínculo é a linha de `insumos_empresa`: é ela que diz que aquela empresa
 * compra aquele insumo. Sem nenhuma, o insumo não é de ninguém — não entra em
 * solicitação, pedido nem nota —, e por isso o cadastro exige ao menos uma
 * (`.spec/015`, decisão 10).
 *
 * O campo que a linha carrega é decisão de **compra**, não de local: vale para
 * a empresa inteira, e reparti-lo por depósito faria o mesmo insumo aceitar
 * excedente num canteiro e não no outro.
 *
 * **O consumo automático não está mais aqui** (`.spec/015`, decisão 50): ele é
 * da operação fiscal do item da nota. Consumir ao chegar descreve o que a
 * entrada significa, e não o material — ver `criarOperacaoFiscalSchema`.
 */
export const vinculoEmpresaSchema = z.object({
  empresaId: uuidSchema,
  /**
   * Tolerância de pedido acima do solicitado, em percentual.
   *
   * Nulo — o padrão — usa a política da conta. É o caso comum: quem cadastra o
   * insumo raramente sabe de cabeça em que fração o fornecedor fecha o palete.
   */
  percentualToleranciaQuantidade: percentualSchema.nullable().prefault(null),
});

export type VinculoEmpresa = z.infer<typeof vinculoEmpresaSchema>;

/** O vínculo com empresa como ele volta da leitura. */
export const insumoEmpresaSchema = z.object({
  empresaId: uuidSchema,
  /** Desnormalizado na leitura, para a tela não pedir as empresas à parte. */
  empresaNome: z.string().nullable(),
  percentualToleranciaQuantidade: percentualSchema.nullable(),
  ativo: z.boolean(),
});

export type InsumoEmpresa = z.infer<typeof insumoEmpresaSchema>;

/* -------------------------------------------------------------------------- */
/* Vínculo com depósito                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Em quais depósitos este material existe.
 *
 * O vínculo é a linha de `insumos_deposito` (`.spec/015`, decisão 8). O cadastro
 * cria e remove a linha e define **um único campo** dela: a quantidade de
 * reposição. Saldo, custo médio e último custo pertencem à movimentação de
 * estoque, e não entram no payload — o que não existe no tipo não tem como ser
 * gravado por engano.
 *
 * Só material. Serviço não movimenta estoque, então não mora em lugar nenhum.
 */
/**
 * Ponto de reposição: **nunca negativo**.
 *
 * `quantidadeSchema` aceita sinal, e faltava a barreira aqui como faltava no
 * valor unitário do lançamento de estoque. O estrago é mais quieto: um mínimo de
 * −50 nunca é atingido, então o alerta de reposição simplesmente deixa de
 * existir — e quem cadastrou continua esperando ser avisado quando o material
 * acabar.
 *
 * Desligar o alerta é legítimo e tem forma própria: **zero**, que é o padrão.
 * Por isso `>= 0`, e não `> 0`.
 */
const quantidadeDeReposicaoSchema = quantidadeSchema.refine(
  (v) => Number(v) >= 0,
  'A quantidade de reposição não pode ser negativa. Use zero para desligar o alerta deste depósito.',
);

export const vinculoDepositoSchema = z.object({
  depositoId: uuidSchema,
  /**
   * Mínimo que dispara o alerta, **por local**.
   *
   * Zero desliga o alerta daquele depósito — é o padrão, e é o que se espera de
   * um material recém-cadastrado, cujo consumo ainda ninguém conhece. Negativo
   * não desliga, some: ver `quantidadeDeReposicaoSchema`.
   */
  quantidadeEstoqueReposicao: quantidadeDeReposicaoSchema.prefault('0'),
});

export type VinculoDeposito = z.infer<typeof vinculoDepositoSchema>;

/** O vínculo como ele volta da leitura, com os saldos que só se exibem. */
export const insumoDepositoSchema = z.object({
  depositoId: uuidSchema,
  /** Desnormalizados na leitura, para a tela não pedir os depósitos à parte. */
  depositoNome: z.string(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  depositoAtivo: z.boolean(),
  quantidadeEstoqueReposicao: quantidadeSchema,
  /**
   * Saldos, **somente leitura**.
   *
   * Voltam como string decimal, nunca como number: ponto flutuante em cálculo
   * de estoque perde centavos de forma silenciosa. Enquanto o módulo de estoque
   * não existe, são todos zero — e é assim que a tela os mostra, sem fingir que
   * há movimento.
   */
  quantidadeEstoque: quantidadeSchema,
  /** Seis casas: é taxa, não dinheiro. Ver `custoUnitarioSchema`. */
  valorCustoMedio: custoUnitarioSchema,
  /**
   * O preço da última entrada. **Seis** casas.
   *
   * Dizia "duas casas: preço é dinheiro", e a frase caiu junto com a premissa:
   * combustível é vendido com seis casas no litro, e guardar o último preço em
   * centavos faria a próxima nota do posto voltar arredondada como referência.
   * Ver `precoUnitarioSchema`.
   */
  valorUltimoCusto: precoUnitarioSchema,
  valorEstoque: dinheiroSchema,
});

export type InsumoDeposito = z.infer<typeof insumoDepositoSchema>;

/* -------------------------------------------------------------------------- */
/* Resumo de estoque                                                          */
/* -------------------------------------------------------------------------- */

/**
 * O estoque do insumo **somado**, para a listagem não precisar dos vínculos.
 *
 * O recorte é o das **empresas vinculadas ao insumo**: soma-se `insumos_deposito`
 * dos depósitos dessas empresas, e não de todo depósito onde a linha exista. Um
 * vínculo de empresa desfeito deixa a linha de saldo para trás — ela não pode
 * ser apagada enquanto tiver saldo —, e contá-la aqui mostraria material de uma
 * empresa que, segundo o próprio cadastro, não usa mais o insumo.
 *
 * Tudo em string decimal, como o resto dos números do sistema. Serviço não
 * movimenta estoque: os quatro valores vêm zerados e a data, nula.
 *
 * Os campos são espalhados dentro de `insumoSchema` em vez de virem num objeto
 * aninhado porque a tabela os mostra como colunas irmãs das outras — um
 * `insumo.estoque.quantidade` obrigaria toda célula a checar se o objeto veio.
 */
const resumoDeEstoque = {
  /** Soma de `quantidade_estoque` dos depósitos das empresas do insumo. */
  quantidadeEstoque: quantidadeSchema,
  /** Soma de `valor_estoque` dos mesmos depósitos. */
  valorEstoque: dinheiroSchema,
  /**
   * Custo médio do insumo no grupo: valor do estoque ÷ quantidade em estoque.
   *
   * Calculado na consulta, nunca gravado — é a média ponderada dos depósitos, e
   * materializá-la criaria um terceiro número para discordar dos outros dois.
   * Sem saldo não há média a fazer, e o campo vem zerado.
   *
   * Seis casas, como o custo médio da linha: é taxa, não dinheiro.
   */
  valorCustoMedio: custoUnitarioSchema,
  /**
   * Custo unitário da **última entrada**, e não o médio.
   *
   * Vem da movimentação, não de `insumos_deposito.valor_ultimo_custo`: aquela
   * coluna é por depósito, e escolher entre elas exigiria saber qual recebeu
   * material por último — que é exatamente o que esta consulta já responde.
   *
   * **Seis casas.** Estava declarado como dinheiro de duas, e isso já estava
   * errado antes desta mudança: `movimentacoes_estoque.valor_unitario`, que é a
   * origem, sempre teve seis. A divergência não estourava porque a resposta não
   * passa por `parse` — o schema documenta e não valida a saída —, então o
   * cliente recebia seis casas num campo anunciado com duas.
   */
  valorUltimaEntrada: precoUnitarioSchema,
  /**
   * Dia da última entrada de estoque, em qualquer um desses depósitos.
   *
   * Nula quando o insumo nunca entrou: material recém-cadastrado, ou serviço.
   * Traço na tela, não uma data qualquer.
   */
  dataUltimaEntrada: dataSchema.nullable(),
} as const;

/** Os campos que só existem com `servicoPrestado` marcado. */
export const CAMPOS_DO_SERVICO_PRESTADO = [
  'codigoTributacaoNacional',
  'nbs',
  'codigoTributacaoMunicipal',
  'aliquotaIss',
] as const;

/**
 * Cadastro do insumo.
 *
 * O `superRefine` guarda as regras que cruzam campos, todas decorrentes do
 * **tipo**: serviço não tem NCM e serviço não tem depósito. A tela já esconde os
 * dois campos quando o tipo é serviço, mas esconder é conveniência — quem recusa
 * é aqui, e os `CHECK` do banco são a rede final. Sem a checagem no contrato, um
 * `curl` gravaria um serviço com nomenclatura de mercadoria e saldo de estoque,
 * e o erro só apareceria numa conferência fiscal meses depois.
 */
export const criarInsumoSchema = z
  .object({
    /**
     * O código reduzido. **Obrigatório, e sem queda para vazio.**
     *
     * Não tem `.prefault()` nem `.optional()` de propósito: o valor vem sugerido
     * pelo servidor (`GET /insumos/proximo-codigo`), então "não informei" não é
     * um estado que a tela produza. Um padrão aqui — o próximo sequencial,
     * calculado no contrato — seria impossível: o contrato não conhece o
     * catálogo da conta, e dois cadastros abertos ao mesmo tempo receberiam o
     * mesmo número sem ninguém ter escolhido isso.
     */
    codigo: codigoInsumoSchema,
    nome: z
      .string()
      .trim()
      .min(2, 'Informe o nome do insumo.')
      .max(
        TAMANHO_MAXIMO_NOME_INSUMO,
        `O nome tem no máximo ${TAMANHO_MAXIMO_NOME_INSUMO} caracteres.`,
      ),
    grupoInsumoId: uuidSchema,
    tipo: tipoInsumoSchema,
    /**
     * Em que o insumo se conta. **Obrigatória nos dois tipos.**
     *
     * Sem ela a quantidade é número solto: "12 de cimento" não distingue doze
     * sacos de doze quilos. Serviço também tem — hora, diária, mês —, e é por
     * isso que a unidade não segue o NCM em ficar restrita a material.
     */
    unidadeMedidaId: uuidSchema,
    /**
     * Classificação fiscal, **opcional**.
     *
     * Quem cadastra o insumo raramente sabe o NCM na hora. Exigi-lo faria o
     * usuário inventar um código para conseguir gravar — pior do que não ter
     * classificação, porque código errado passa pela conferência sem levantar
     * suspeita.
     */
    ncmCodigo: codigoNcmSchema.nullable().prefault(null),
    /**
     * Quantas casas o preço deste insumo tem — de 2 a 6, **2 quando omitido**.
     *
     * A coluna de preço aceita seis para todo mundo (ver `precoUnitarioSchema`),
     * e é este campo que diz quantas fazem sentido naquele material: o litro de
     * diesel é vendido a `6,123456`, o saco de cimento a `32,50`. Sem ele, ou o
     * cimento passaria a pedir quatro zeros por linha, ou o diesel continuaria
     * truncado — não há um número que sirva aos dois.
     *
     * **Vale para serviço também**, ao contrário do NCM: hora de subempreitada
     * com preço fechado em centavos é o caso comum, mas a diária rateada por
     * equipe cai em fração, e nada na natureza do serviço decide isso por ele.
     *
     * O padrão é 2 e não precisa ser digitado: o cadastro que existia antes deste
     * campo se comporta como sempre se comportou.
     */
    casasDecimaisPreco: casasDecimaisPrecoSchema.prefault(CASAS_PRECO_PADRAO),
    /**
     * Conta de custo ou despesa em que o insumo se classifica, **opcional**.
     *
     * É o que permite comparar orçado com realizado por natureza de custo e
     * apurar o DRE (`.spec/015`, decisão 13). Opcional pelo mesmo motivo do
     * NCM: exigi-la faria quem não sabe a conta escolher qualquer uma para
     * conseguir gravar, e classificação errada entra no balancete sem levantar
     * suspeita — ausente, ao menos, aparece na conferência.
     *
     * Vale para os dois tipos: serviço também tem custo, e a subempreitada é
     * uma das contas mais movimentadas da obra.
     *
     * Que a conta seja **folha** do plano é checado no service: depende de
     * saber se ela tem filhas, e isso só o servidor sabe.
     */
    contaContabilId: uuidSchema.nullable().prefault(null),
    descricao: z.string().trim().max(2000).nullable().prefault(null),
    /**
     * O material entra em **lista de compra direta** (`.spec/015`, decisão 46).
     *
     * Compra direta é o caminho curto: a obra monta a lista, o Compras gera o
     * pedido, e a solicitação e a cotação ficam de fora. Só vale para o material
     * cujo fornecedor e preço a construtora já negociou — e é justamente isso que
     * os dois campos abaixo guardam.
     *
     * Nasce **desmarcada**. O caminho normal da compra passa por cotação, e um
     * material que entrasse na compra direta sem ninguém ter decidido isso viraria
     * pedido a um fornecedor que talvez não seja mais o melhor preço.
     */
    permiteCompraDireta: z.boolean().prefault(false),
    /**
     * De quem se compra, e por quanto. **Vazios, nulos e ausentes são a mesma
     * coisa** — o `<select>` devolve `''` quando nada foi escolhido, e sem a
     * união isso falharia com uma mensagem sobre formato de uuid num campo que o
     * usuário deixou em branco de propósito.
     *
     * Um fornecedor só, e da conta inteira. Quem tiver dois acordos para o mesmo
     * material usa a cotação, que é o instrumento que compara fornecedores.
     */
    fornecedorCompraDiretaId: uuidSchema
      .or(z.literal(''))
      .nullable()
      .transform((valor) => (valor === '' || valor === null ? null : valor))
      .prefault(null),
    /**
     * Preço negociado por unidade — **seis casas**, como todo preço de compra.
     *
     * O sinal é lido do texto, e não de `Number(v) >= 0`. Em Zod 3 a falha da
     * regex de `precoUnitarioSchema` deixa o resultado *dirty*, e não *aborted*:
     * o `refine` roda mesmo assim, sobre o texto que a regex já recusou. Com
     * "1.250,50" o `Number` devolvia `NaN`, `NaN >= 0` era falso, e a tela
     * recebia "não pode ser negativo" para um preço positivo mal formatado —
     * mensagem que não tem como ser seguida, porque o defeito era o separador.
     */
    valorCompraDireta: precoUnitarioSchema
      .refine((v) => !v.startsWith('-'), 'O preço negociado não pode ser negativo.')
      .or(z.literal(''))
      .nullable()
      .transform((valor) => (valor === '' || valor === null ? null : valor))
      .prefault(null),
    /**
     * O serviço é **prestado** pela construtora — entra em nota fiscal de
     * serviço emitida (`.spec/015`, decisão 88).
     *
     * Marca no insumo, e não cadastro à parte: o serviço que a empresa vende e o
     * que ela compra são o mesmo vocabulário — "locação de betoneira" está nos
     * dois lados numa construtora que subempreita —, e um segundo catálogo
     * obrigaria a cadastrar o mesmo nome duas vezes. O item de orçamento
     * (`CAD·16`) foi avaliado para este papel e recusado: ele é como a proposta
     * se **apresenta**, e não tem código fiscal nenhum por desenho (decisão 58).
     *
     * **Só serviço.** Material vendido sai por NF-e, que é outro documento.
     */
    servicoPrestado: z.boolean().prefault(false),
    /**
     * Os quatro dados fiscais do serviço prestado. **Todos opcionais** — quem
     * cadastra o serviço raramente tem a lista da LC 116 aberta, e a nota guarda
     * a própria cópia: o que falta aqui se preenche lá.
     */
    codigoTributacaoNacional: codigoTributacaoNacionalSchema,
    nbs: nbsSchema,
    codigoTributacaoMunicipal: codigoTributacaoMunicipalSchema,
    /** Alíquota de ISS que a nota sugere (%). Sugestão: a nota grava a dela. */
    aliquotaIss: aliquotaOpcionalSchema,
    /**
     * Empresas que usam este insumo. **Ao menos uma**, sempre.
     *
     * A lista é o estado final, não um delta: o que não vem aqui é
     * desvinculado. Um payload parcial obrigaria o cliente a mandar "remova a
     * empresa X" separadamente, e duas telas abertas ao mesmo tempo acabariam
     * somando vínculos que ninguém pediu.
     */
    empresas: z.array(vinculoEmpresaSchema).prefault([]),
    /**
     * Depósitos em que o material existe. Mesma mecânica de estado final.
     *
     * Cada depósito precisa pertencer a uma das empresas acima, e cada empresa
     * precisa ter ao menos um — as duas checagens dependem de saber a empresa de
     * cada depósito, que só o servidor tem, então moram no service.
     */
    depositos: z.array(vinculoDepositoSchema).prefault([]),
    ativo: z.boolean().prefault(true),
  })
  .superRefine((valor, ctx) => {
    /*
      Serviço prestado é coisa de serviço, e os códigos são coisa de serviço
      prestado. A tela esconde os campos nos outros casos; quem recusa é aqui, e
      os dois `CHECK` da tabela são a rede final. Sem isto um material sairia do
      catálogo com código da LC 116, e o seletor da nota de serviço o ofereceria.
    */
    if (valor.servicoPrestado && valor.tipo !== 'SERVICO') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['servicoPrestado'],
        message: 'Só serviço entra em nota fiscal de serviço. Material vendido sai por NF-e.',
      });
    }

    if (!valor.servicoPrestado) {
      for (const campo of CAMPOS_DO_SERVICO_PRESTADO) {
        if (valor[campo] === null) continue;

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [campo],
          message: 'Este dado é do serviço prestado: marque "serviço prestado" ou deixe em branco.',
        });
      }
    }

    if (valor.tipo === 'SERVICO' && valor.ncmCodigo !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ncmCodigo'],
        message: 'Serviço não tem NCM: a Nomenclatura Comum do Mercosul classifica mercadoria.',
      });
    }

    if (valor.tipo === 'SERVICO' && valor.depositos.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['depositos'],
        message: 'Serviço não movimenta estoque: não se guarda serviço em depósito.',
      });
    }

    // Vale para os dois tipos. Insumo sem empresa não é de ninguém: não entra
    // em solicitação, pedido nem nota, e ficaria no catálogo como registro que
    // parece cadastrado e não serve para nada.
    if (valor.empresas.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['empresas'],
        message: 'Escolha ao menos uma empresa que usa este insumo.',
      });
    }

    acusarCompraDiretaIncompleta(valor, ctx);

    // Os índices únicos recusariam de qualquer forma, com uma violação de
    // constraint que o usuário não consegue interpretar. Aqui o erro aponta o
    // campo.
    acusarDuplicados(
      ctx,
      valor.empresas.map((v) => v.empresaId),
      'empresas',
      'Esta empresa aparece duas vezes na lista.',
    );
    acusarDuplicados(
      ctx,
      valor.depositos.map((v) => v.depositoId),
      'depositos',
      'Este depósito aparece duas vezes na lista.',
    );
  });

/**
 * A compra direta é o trio inteiro, ou nada.
 *
 * O erro aponta o **campo em branco**, e não a caixa: quem está incompleto é o
 * vínculo, e apontar `permiteCompraDireta` mandaria desmarcar a decisão que o
 * usuário acabou de tomar. É o mesmo tratamento do tipo de movimentação na
 * operação fiscal.
 *
 * O caminho inverso é livre: fornecedor e preço podem sobrar com a caixa
 * desmarcada, e assim quem suspendeu a compra direta de um material reencontra o
 * acordo ao remarcar, em vez de redigitá-lo.
 *
 * Função de módulo, e não duas ramificações dentro do `superRefine`: as quatro
 * condições somadas às que já estavam lá passavam o limite de complexidade do
 * lint.
 */
function acusarCompraDiretaIncompleta(
  valor: {
    permiteCompraDireta: boolean;
    fornecedorCompraDiretaId: string | null;
    valorCompraDireta: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (!valor.permiteCompraDireta) return;

  if (valor.fornecedorCompraDiretaId === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['fornecedorCompraDiretaId'],
      message: 'Escolha o fornecedor: o pedido gerado da lista precisa saber a quem se dirige.',
    });
  }

  if (valor.valorCompraDireta === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['valorCompraDireta'],
      message:
        'Informe o preço negociado: é ele que vai para o item do pedido, e a compra direta não passa por cotação para descobri-lo.',
    });
  }
}

/** Acusa ids repetidos numa lista de vínculos, apontando a posição. */
function acusarDuplicados(
  ctx: z.RefinementCtx,
  ids: readonly string[],
  campo: 'empresas' | 'depositos',
  mensagem: string,
): void {
  const vistos = new Set<string>();

  for (const [indice, id] of ids.entries()) {
    if (vistos.has(id)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [campo, indice], message: mensagem });
    }
    vistos.add(id);
  }
}

export type CriarInsumo = z.infer<typeof criarInsumoSchema>;
export const atualizarInsumoSchema = criarInsumoSchema;
export type AtualizarInsumo = z.infer<typeof atualizarInsumoSchema>;

export const insumoSchema = z.object({
  id: uuidSchema,
  /**
   * O código reduzido, sempre presente e sempre em caixa alta.
   *
   * Vem na **listagem** e não só no detalhe, pelo mesmo motivo de
   * `casasDecimaisPreco`: os seletores de insumo se alimentam da listagem, e é
   * ali que o código precisa aparecer — a busca por código só ajuda se quem
   * digitou "1042" vê "1042" na linha que escolheu.
   */
  codigo: z.string(),
  nome: z.string(),
  grupoInsumoId: uuidSchema,
  /**
   * Nome do grupo, desnormalizado na leitura.
   *
   * A listagem mostra a classificação sem uma segunda consulta por linha — que,
   * numa página de 25, seriam 25 idas ao banco para exibir uma coluna.
   */
  grupoNome: z.string(),
  tipo: tipoInsumoSchema,
  unidadeMedidaId: uuidSchema,
  /**
   * Sigla e marca de indivisibilidade, desnormalizadas na leitura.
   *
   * A sigla porque toda quantidade do insumo se lê ao lado dela — "120" e
   * "120 SC" não são a mesma informação. A marca porque é ela que decide se o
   * lançamento de estoque aceita fração, e a tela precisa avisar **antes** de o
   * usuário digitar, não depois do 422.
   */
  unidadeSigla: z.string(),
  unidadeApenasInteiros: z.boolean(),
  ncmCodigo: z.string().nullable(),
  /**
   * Casas do preço deste insumo. Sempre presente — 2 é o padrão, não a ausência.
   *
   * Vem na **listagem** e não só no detalhe, e é de propósito: quem monta uma
   * cotação escolhe o insumo pelo seletor, que se alimenta da listagem, e o
   * campo de preço precisa saber a escala no instante em que a linha é criada —
   * antes de qualquer consulta por id.
   */
  casasDecimaisPreco: z.number().int(),
  /**
   * Descrição do NCM, desnormalizada pelo mesmo motivo do grupo.
   *
   * É a descrição **completa**, com o caminho da hierarquia: "-- Outros",
   * sozinho, não diz a ninguém que classificação foi escolhida. Vem nula
   * quando não há NCM.
   */
  ncmDescricao: z.string().nullable(),
  /** Entra em nota fiscal de serviço emitida. Só em serviço. */
  servicoPrestado: z.boolean(),
  codigoTributacaoNacional: z.string().nullable(),
  nbs: z.string().nullable(),
  codigoTributacaoMunicipal: z.string().nullable(),
  aliquotaIss: z.string().nullable(),
  contaContabilId: uuidSchema.nullable(),
  /**
   * Código e descrição da conta, desnormalizados na leitura.
   *
   * Os dois juntos pelo mesmo motivo do seletor de conta contábil: "Cimento,
   * areia, brita e argamassas" identifica, mas o código é o que o contador
   * procura, e o mesmo nome de conta existe em mais de um ramo do plano.
   * Nulos quando o insumo não está classificado.
   */
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  descricao: z.string().nullable(),
  /**
   * Compra direta: a caixa e o acordo.
   *
   * Vêm na **listagem** e não só no detalhe, e é de propósito: o seletor de
   * material da lista de compra direta se alimenta da listagem, e ele precisa do
   * fornecedor e do preço no instante em que a linha é criada — antes de
   * qualquer consulta por id. É o mesmo motivo de `casasDecimaisPreco`.
   *
   * **Sem o nome do fornecedor desnormalizado**, ao contrário do grupo e da
   * conta contábil. A junção o traria em toda leitura de insumo para servir a uma
   * tela só, e a que precisa dele — a lista de compra direta — já carrega os
   * fornecedores para o próprio seletor. Nulos no material que não permite
   * compra direta.
   */
  permiteCompraDireta: z.boolean(),
  fornecedorCompraDiretaId: uuidSchema.nullable(),
  valorCompraDireta: precoUnitarioSchema.nullable(),
  ativo: z.boolean(),
  ...resumoDeEstoque,
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type Insumo = z.infer<typeof insumoSchema>;

/**
 * O insumo com os vínculos de empresa e de depósito.
 *
 * Separado de `insumoSchema` de propósito: só a leitura de **um** insumo carrega
 * os vínculos. Devolvê-los na listagem custaria duas consultas a mais por página
 * para preencher colunas que a tabela não mostra — e campos que voltam sempre
 * vazios na lista seriam uma promessa que a tela leria como fato.
 *
 * `empresas` nunca vem vazia num insumo gravado. `depositos` é sempre vazia em
 * serviço.
 */
export const insumoDetalhadoSchema = insumoSchema.extend({
  empresas: z.array(insumoEmpresaSchema),
  depositos: z.array(insumoDepositoSchema),
});

export type InsumoDetalhado = z.infer<typeof insumoDetalhadoSchema>;

export const listaInsumosSchema = respostaListaSchema(insumoSchema);
export type ListaInsumos = z.infer<typeof listaInsumosSchema>;

/**
 * Listagem, com cinco filtros além dos comuns.
 *
 * `q` faz duas perguntas de uma vez, e não há filtro separado de código: a
 * busca textual sobre nome e descrição **ou** o prefixo do código reduzido. Um
 * `codigo` próprio na query obrigaria a tela a decidir, antes de mandar, se o
 * que o usuário digitou é código ou nome — e no caixa de busca de um seletor
 * não há como saber: "25" é o começo de um código e também de "25mm".
 *
 * `tipo` porque quem confere estoque só quer material, e `grupoInsumoId`
 * porque a pergunta frequente é "o que existe dentro deste grupo?". Nenhum dos
 * dois se resolve com o campo de busca: o nome do insumo não carrega nem o
 * tipo nem o grupo.
 *
 * O filtro por grupo recorta o **ramo inteiro** — "dentro de Elétrica" inclui
 * "Elétrica › Cabos", que é o que a palavra significa na árvore ao lado.
 *
 * `empresaId` é o filtro dos **documentos**: sem vínculo com a empresa, o insumo
 * não entra em solicitação, pedido nem nota (`.spec/015`, decisão 10). Oferecer
 * o catálogo inteiro no formulário de compra só serviria para o usuário
 * descobrir isso depois de preencher a linha. O recorte é pelo vínculo **ativo**
 * dos dois lados — o insumo e a linha de `insumos_empresa`.
 *
 * `depositoId` é o filtro dos **movimentos de estoque**: só o material
 * **vinculado** àquele depósito (`.spec/015`, decisão 51). Quem decide onde o
 * material pode ficar é o cadastro do insumo, e o lançamento de estoque pergunta
 * o depósito antes do material — oferecer o catálogo inteiro depois disso só
 * serviria para o usuário descobrir `insumo_nao_vinculado_ao_deposito` depois de
 * digitar a quantidade. Vínculo, e não saldo: uma **entrada** é legítima sobre
 * linha zerada, e é assim que todo material entra no depósito a primeira vez.
 *
 * `comEstoque` é o filtro de **quem consulta estoque**, não de quem cadastra: o
 * cardex e a contagem física perguntam "o que existe hoje", e num catálogo de
 * 331 materiais a maioria nunca teve movimento. Vale `true` para saldo
 * **maior que zero** — zerado fica de fora, porque material zerado é o que se
 * quer justamente pular ao conferir prateleira.
 *
 * **Com `depositoId`, o `comEstoque` olha só aquele depósito**; sem ele, soma os
 * depósitos das empresas vinculadas, como sempre. É o par que o consumo de obra
 * usa: só o que se pode retirar **daquele** almoxarifado. A alternativa era um
 * segundo booleano (`comSaldoNoDeposito`) ao lado deste; foi descartada porque
 * ele só faria sentido acompanhado de `depositoId`, e uma chave que não filtra
 * nada quando vem sozinha é a espécie de parâmetro que se manda errado sem o
 * servidor reclamar. Aqui não há combinação inválida: "com saldo" é sempre "com
 * saldo no recorte pedido".
 *
 * Ele é distinto de `ativo`, e os dois se cruzam: material ativo sem saldo é o
 * caso comum do catálogo recém-importado, e material inativo com saldo é o que
 * saiu de linha e ainda está no galpão. Nenhum dos dois responde pelo outro.
 *
 * `permiteCompraDireta` é o filtro da **lista de compra direta** (`.spec/015`,
 * decisão 46): só o material com fornecedor e preço acordados entra nela. Ele
 * existe no servidor, e não como filtro de tela, pelo motivo de sempre — o
 * catálogo padrão tem 331 materiais e a paginação aceita 200, então recortar no
 * cliente esconderia em silêncio tudo o que ficasse além do corte alfabético.
 *
 * `fornecedorId` é o filtro do **item do pedido de compra** (`.spec/015`,
 * decisão 87): só o que aquele fornecedor oferece — os ramos dos "Grupos de
 * insumo oferecidos" do cadastro dele, menos os insumos não ofertados.
 * **Fornecedor sem grupo marcado não recorta nada**: o cadastro que nunca
 * respondeu à pergunta não afirmou que o fornecedor não vende coisa alguma, e
 * lista vazia ali pareceria catálogo vazio. Pelo mesmo motivo a lista de não
 * ofertados só vale **com** grupo marcado — ela é exceção à regra do grupo, e
 * sem a regra não há do que excetuar.
 */
export const listarInsumosQuerySchema = listagemDeCadastroSchema.extend({
  tipo: tipoInsumoSchema.optional(),
  grupoInsumoId: uuidSchema.optional(),
  empresaId: uuidSchema.optional(),
  /** Só o material vinculado a este depósito. Dá escopo ao `comEstoque`. */
  depositoId: uuidSchema.optional(),
  comEstoque: booleanoDeQuerySchema,
  permiteCompraDireta: booleanoDeQuerySchema,
  /** Só o que o fornecedor oferece — sem grupo marcado no cadastro dele, tudo. */
  fornecedorId: uuidSchema.optional(),
  /**
   * Só os serviços prestados — o recorte do seletor da nota de serviço.
   *
   * Recortado pelo servidor, como `permiteCompraDireta`: o catálogo passa de
   * mil insumos, e filtrar na tela mostraria uma página de materiais com "nada
   * encontrado" embaixo.
   */
  servicoPrestado: booleanoDeQuerySchema,
});

export type ListarInsumosQuery = z.infer<typeof listarInsumosQuerySchema>;

/**
 * Colunas de ordenação da listagem.
 *
 * `relevancia` não é coluna: é o `ts_rank` da busca textual, e só faz sentido
 * com um termo digitado — sem ele, o servidor volta para o nome. É a ordem
 * padrão enquanto se procura, porque alfabética durante uma busca é ordem de
 * lista telefônica: correta e inútil.
 *
 * `codigo` ordena como **texto**, e não como número: a coluna é `varchar` e
 * aceita `CIM-01` ao lado de `1042` (`.spec/015`, decisão 49). Numa conta que
 * só usa o sequencial sugerido isso deixa "10" antes de "9" — o preço de o
 * código ser livre, e a saída de quem se incomoda é digitar o número com
 * largura fixa ("0009"), que o cadastro aceita.
 */
export const COLUNAS_ORDENAVEIS_INSUMO = [
  'relevancia',
  'codigo',
  'nome',
  'tipo',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Sugestão de classificação                                                  */
/* -------------------------------------------------------------------------- */

/**
 * O assistente de classificação do cadastro de insumo (`.spec/015`, decisão 28).
 *
 * Responde a uma pergunta só: **em que grupo e em que conta contábil este
 * material se parece com os que já existem?** Não é conhecimento sobre
 * construção civil — é precedente, e é por isso que a resposta carrega a
 * evidência junto.
 *
 * ## Por que isto não contradiz a decisão 20
 *
 * A decisão 20 mediu e recusou o casamento por descrição: 17 de 519 descrições
 * de fornecedor casaram por texto, e concluiu que "um casamento que acerta 3%
 * não é ajuda". Lá o texto decidia **identidade** — qual material é este —, e o
 * erro entrava como material no insumo errado, gravado, com aparência de
 * conferido.
 *
 * Aqui o texto decide **classificação sugerida**, num campo à vista, que o
 * usuário lê antes de gravar e corrige com um clique. O custo do erro é outro, e
 * é o que muda a conta: sugerir errado custa uma correção; casar errado custa
 * estoque no lugar errado descoberto meses depois.
 *
 * A régua que separa as duas continua valendo: **sugestão nunca grava calada**.
 */
// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const sugerirClassificacaoQuerySchema = z
  .object({
    /** O nome digitado. É a busca textual, a mesma da listagem. */
    nome: z.string().trim().max(TAMANHO_MAXIMO_NOME_INSUMO).optional(),
    /**
     * O NCM, quando já preenchido. **Desempate, e não fonte primária.**
     *
     * Ele é classificação **fiscal**, e a pergunta aqui é de **custo** — os dois
     * nem sempre coincidem. Vale como corte porque acerta justamente onde o nome
     * erra: "manta asfáltica" e "manta térmica" são a mesma palavra e materiais
     * diferentes, e o NCM os separa (6807 contra 7607).
     */
    ncmCodigo: codigoNcmSchema.optional(),
    /**
     * Material ou serviço, quando já escolhido.
     *
     * Recorta antes de tudo: nenhum precedente de serviço explica um material, e
     * as contas dos dois vivem em ramos diferentes do plano.
     */
    tipo: tipoInsumoSchema.optional(),
  })
  .strict();

export type SugerirClassificacaoQuery = z.infer<typeof sugerirClassificacaoQuerySchema>;

/**
 * A sugestão, **com a evidência que a sustenta**.
 *
 * Os números e os exemplos não são enfeite: é o que permite ao usuário discordar
 * sem abrir outra tela. "Cimento" sugerido em "Coberturas e impermeabilização"
 * porque três insumos parecidos estão lá é uma frase que se aceita ou se recusa;
 * "sugerido" sozinho não é.
 *
 * `grupo` e `conta` são independentes, e a diferença aparece o tempo todo: a
 * conta contábil é opcional no cadastro, então há muito insumo com grupo e sem
 * classificação. O grupo sai mesmo assim, e é metade do trabalho poupado.
 *
 * ## A fonte é o cadastro da própria conta, e só ele
 *
 * O catálogo padrão foi cogitado como queda para quem ainda não cadastrou nada
 * parecido, e **não sobreviveu ao desenho**: os precedentes dele são nomes de
 * grupo e códigos de conta que só existem depois da importação — e quem importa
 * ganha 331 insumos de uma vez, que é justamente o precedente que faltava. A
 * queda serviria a um caso estreito (grupos criados à mão com os nomes do
 * padrão) e resolveria o id do grupo por nome, cujo erro é silencioso: casaria
 * um grupo homônimo em outro ramo da árvore.
 *
 * Então a resposta honesta na conta vazia é o silêncio, e é o que ela devolve.
 */
export const sugestaoDeClassificacaoSchema = z.object({
  /** Quantos precedentes entraram na conta. Zero quando não houve. */
  precedentes: z.number().int(),
  /** Até três nomes, para a tela dizer com base em quê. */
  exemplos: z.array(z.string()),
  grupo: z
    .object({
      id: uuidSchema,
      /**
       * O nome do **nó**, e não o caminho inteiro na árvore.
       *
       * "Coberturas e impermeabilização" basta para o usuário reconhecer o
       * grupo, e o caminho completo custaria uma consulta recursiva para dizer
       * o que o próprio seletor mostra assim que o campo é preenchido.
       */
      nome: z.string(),
      /** Quantos dos precedentes estão neste grupo. */
      quantos: z.number().int(),
    })
    .nullable(),
  conta: z
    .object({
      id: uuidSchema,
      codigoInterno: z.string(),
      descricao: z.string(),
      quantos: z.number().int(),
    })
    .nullable(),
});

export type SugestaoDeClassificacao = z.infer<typeof sugestaoDeClassificacaoSchema>;

/* -------------------------------------------------------------------------- */
/* Catálogo de insumos padrão                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Importação do catálogo de insumos padrão de construção civil.
 *
 * Traz a **árvore de grupos** junto com os insumos: sem os grupos os insumos
 * não teriam onde pendurar, e `grupo_insumo_id` é obrigatório.
 *
 * `empresaIds` é obrigatório e não tem padrão. Insumo sem empresa não entra em
 * solicitação, pedido nem nota (`.spec/015`, decisão 10) — importar 331 deles
 * sem vínculo encheria o cadastro de registros que parecem prontos e não
 * servem para nada. Quem importa escolhe a quais empresas o catálogo pertence,
 * e a escolha vale para o lote inteiro.
 *
 * `sobrescrever` decide o que fazer com um nome que já está lá: por padrão o
 * insumo existente é preservado e o do catálogo é pulado. Verdadeiro, a
 * descrição, o grupo e o NCM do catálogo passam por cima — o **tipo nunca
 * muda** (`.spec/015`, decisão 9), nem o id, porque há documento apontando
 * para eles.
 *
 * A **conta contábil vazia é a exceção**: ela é preenchida mesmo sem
 * sobrescrita, porque preencher campo em branco não reescreve escolha nenhuma.
 * É o que permite a quem importou o catálogo antes da classificação contábil
 * existir recebê-la rodando a importação de novo.
 */
export const importarInsumosPadraoSchema = z.object({
  empresaIds: z
    .array(uuidSchema)
    .min(1, 'Escolha ao menos uma empresa que vai usar estes insumos.'),
  sobrescrever: z.boolean().prefault(false),
});

export type ImportarInsumosPadrao = z.infer<typeof importarInsumosPadraoSchema>;

/**
 * O que a importação fez, em números.
 *
 * Grupos e insumos são contados separado porque falham por motivos diferentes:
 * um grupo já existente é reaproveitado em silêncio — é o comportamento certo,
 * e não uma exceção —, enquanto um insumo já existente é uma decisão do
 * usuário sobre sobrescrever ou não.
 *
 * `semNcm` conta os materiais que entraram sem classificação porque o código
 * do catálogo não estava em `ncms`. Zero é o caso normal; qualquer outro
 * número é o aviso de que o catálogo NCM da instalação está atrasado em
 * relação a este catálogo de insumos.
 *
 * `semContaContabil` é o equivalente do lado contábil: insumos que entraram sem
 * classificação de custo porque a conta que o catálogo indica não existe no
 * plano desta conta. É o número esperado — e alto — em quem montou plano
 * próprio, e zero em quem importou o plano padrão.
 */
export const resultadoInsumosPadraoSchema = z.object({
  gruposCriados: z.number().int(),
  gruposReaproveitados: z.number().int(),
  insumosCriados: z.number().int(),
  insumosAtualizados: z.number().int(),
  insumosIgnorados: z.number().int(),
  /**
   * Insumos que já existiam e **ganharam a conta contábil** que estava vazia.
   *
   * Não é subconjunto de `insumosAtualizados`: estes foram preservados, e só o
   * campo em branco foi preenchido. É o número que explica a segunda execução
   * de quem importou o catálogo antes de a classificação contábil existir.
   */
  insumosClassificados: z.number().int(),
  /** Linhas de `insumos_empresa` gravadas — insumos criados × empresas escolhidas. */
  vinculosCriados: z.number().int(),
  /**
   * Linhas de `insumos_deposito` gravadas.
   *
   * Conta os materiais criados **e** os que já existiam: nestes, só as empresas
   * em que o insumo ainda não tinha depósito nenhum — onde o usuário já
   * escolheu, a importação não acrescenta. Serviço não entra, porque não
   * movimenta estoque.
   *
   * É o número que explica a reimportação de quem tinha o catálogo instalado
   * antes de os vínculos passarem a ser criados.
   */
  vinculosDepositoCriados: z.number().int(),
  semNcm: z.number().int(),
  semContaContabil: z.number().int(),
  totalGrupos: z.number().int(),
  totalInsumos: z.number().int(),
});

export type ResultadoInsumosPadrao = z.infer<typeof resultadoInsumosPadraoSchema>;

/**
 * O que a tela precisa saber **antes** de oferecer a importação.
 *
 * Três perguntas que a listagem não responde. `possuiInsumos` decide se o
 * botão entra em destaque ou como ação secundária — oferecer a importação em
 * primeiro plano a quem já montou o cadastro à mão é convidar ao engano.
 *
 * `catalogoNcmImportado` e `possuiEmpresa` são as duas condições de entrada:
 * sem NCM no banco os 299 materiais entrariam sem classificação, e sem empresa
 * não há a quem vincular. A tela desabilita o botão e diz qual das duas falta,
 * em vez de deixar o usuário descobrir pelo erro depois de preencher o
 * diálogo.
 */
export const statusInsumosPadraoSchema = z.object({
  possuiInsumos: z.boolean(),
  catalogoNcmImportado: z.boolean(),
  possuiEmpresa: z.boolean(),
  totalGrupos: z.number().int(),
  totalInsumos: z.number().int(),
});

export type StatusInsumosPadrao = z.infer<typeof statusInsumosPadraoSchema>;
